import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SideDrawer } from '@/components/ui/SideDrawer';
import { Button } from '@/components/ui/Button';
import { outreachAction } from '@/lib/outreach';
import { guessColumns, type ImportColumn, importProblem, parseTable, tableBlock } from '@shared/table-import';
import { AppSourcePicker, type AppSourceTable } from './AppSourcePicker';
import { ImportPreviewTable } from './ImportPreviewTable';
import { CampaignChoice, type CampaignTarget } from './CampaignChoice';

export type ImportSource = 'paste' | 'file' | 'app';

interface TableImportDrawerProps {
  toolAppId: string;
  source: ImportSource;
  /** A table handed over (from the chat), shown ready to check. */
  initialText?: string;
  /** An app's results proposed by the chat: which app, which statuses. */
  initialApp?: { appKey: string; statuses: string[] } | null;
  /** Where the companies go (04/11/2026): a campaign given (from inside it), else a new one by default. */
  initialCampaign?: CampaignTarget;
  /** A campaign's name proposed by the chat. */
  campaignName?: string | null;
  /** From the app's list, an import may fill the base only. */
  allowNoCampaign?: boolean;
  onClose: () => void;
  onImported: () => void;
  /** « Ouvrir la campagne » once imported. */
  onOpenCampaign?: (sequenceId: string) => void;
}

const SKIPPED: Record<string, string> = { no_name: 'sans entreprise', ambiguous: 'entreprise ambiguë' };
const TITLES: Record<ImportSource, string> = { paste: 'Coller un tableau', file: 'Importer un fichier', app: 'Importer les résultats d’une app' };
const today = () => new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

/**
 * Importer (03–04/11/2026), as lemlist imports: a pasted table, a CSV file or
 * the results of another app; check what each column is, see the first rows,
 * choose the campaign, import. A company already in the list is completed,
 * never duplicated; the list stays the base, the campaign names some of it.
 */
export const TableImportDrawer = ({
  toolAppId, source, initialText = '', initialApp = null, initialCampaign, campaignName = null, allowNoCampaign = false, onClose, onImported, onOpenCampaign,
}: TableImportDrawerProps) => {
  const [text, setText] = useState(initialText);
  const [fileName, setFileName] = useState<string | null>(null);
  const [appTable, setAppTable] = useState<AppSourceTable | null>(null);
  const [columns, setColumns] = useState<ImportColumn[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [campaign, setCampaign] = useState<CampaignTarget>(initialCampaign !== undefined ? initialCampaign : { name: campaignName ?? '' });
  const [importedInto, setImportedInto] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // A file import opens the file picker at once.
  useEffect(() => { if (source === 'file') fileInput.current?.click(); }, [source]);

  const pasted = useMemo(() => {
    const [headers = [], ...rows] = parseTable(tableBlock(text));
    return { headers, rows, mapping: guessColumns(headers) };
  }, [text]);
  const table = source === 'app' ? appTable : pasted;
  const headers = table?.headers ?? [];
  const rows = table?.rows ?? [];
  const mapping = columns ?? table?.mapping ?? [];
  const problem = rows.length === 0 ? null : importProblem(mapping);
  const onAppTable = useCallback((next: AppSourceTable | null) => { setAppTable(next); setColumns(null); }, []);
  // A new campaign is named after what is imported: the chat's name, the file, the app and its statuses, else the day.
  const suggestedName = campaignName?.trim()
    || (source === 'file' && fileName ? fileName.replace(/\.[^.]+$/, '') : '')
    || (source === 'app' ? appTable?.label ?? '' : '')
    || `Campagne du ${today()}`;
  const campaignProblem = campaign && 'name' in campaign && !campaign.name.trim() ? 'Donnez un nom à la campagne.' : null;

  const read = (value: string) => {
    setText(value);
    setColumns(null);
    setResult(null);
  };
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const answer = await outreachAction<{
        created: number; updated: number; people: number; skipped: Array<{ row: number; reason: string }>;
        campaign: { sequenceId: string; added: number; inOtherCampaigns: number; otherCampaigns: string[] } | null;
      }>({
        action: 'import_table', toolAppId, columns: mapping, rows,
        ...(campaign ? { campaign: 'name' in campaign ? { name: campaign.name.trim() } : campaign } : {}),
      });
      const left = answer.skipped.length
        ? ` · ${answer.skipped.length} ligne(s) laissée(s) : ${[...new Set(answer.skipped.map((item) => SKIPPED[item.reason] ?? item.reason))].join(', ')}`
        : '';
      const inCampaign = answer.campaign
        ? ` · ${answer.campaign.added} dans la campagne${answer.campaign.inOtherCampaigns ? `, ${answer.campaign.inOtherCampaigns} déjà dans ${answer.campaign.otherCampaigns.map((name) => `« ${name} »`).join(', ')}` : ''}`
        : '';
      setResult(`${answer.created} entreprise(s) ajoutée(s), ${answer.updated} complétée(s), ${answer.people} personne(s)${inCampaign}${left}`);
      setImportedInto(answer.campaign?.sequenceId ?? null);
      onImported();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Import impossible.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SideDrawer label={TITLES[source]} title={TITLES[source]} onClose={onClose}
      actions={rows.length > 0 && !result && (
        <Button size="sm" loading={busy} disabled={busy || Boolean(problem) || Boolean(campaignProblem)} onClick={() => { void run(); }}>
          Importer {rows.length} ligne{rows.length > 1 ? 's' : ''}
        </Button>
      )}>
      <div className="space-y-4 p-4">
        {result && <p role="status" className="text-sm font-semibold text-domino-ink">{result}</p>}
        {result && importedInto && onOpenCampaign && (
          <Button size="sm" variant="secondary" onClick={() => { onOpenCampaign(importedInto); onClose(); }}>Ouvrir la campagne</Button>
        )}
        {error && <p role="alert" className="text-sm text-domino-inkSoft">{error}</p>}
        {!result && (
          <>
            {source === 'paste' && (
              <textarea rows={6} value={text} onChange={(event) => read(event.target.value)} aria-label="Tableau à coller"
                placeholder="Coller un tableau, en-têtes compris"
                className="w-full rounded-lg border border-domino-cardBorder bg-domino-white px-3 py-2 font-mono text-xs text-domino-ink focus:border-domino-aubergine focus:outline-none" />
            )}
            {source === 'file' && (
              <div className="flex items-center gap-3">
                <Button size="sm" variant="secondary" onClick={() => fileInput.current?.click()}>Choisir un fichier</Button>
                {fileName && <span className="truncate text-sm text-domino-ink">{fileName}</span>}
                <input ref={fileInput} type="file" accept=".csv,.tsv,.txt,text/csv" className="sr-only" aria-label="Fichier CSV"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    setFileName(file.name);
                    void file.text().then(read);
                  }} />
              </div>
            )}
            {source === 'app' && (
              <AppSourcePicker toolAppId={toolAppId} initialAppKey={initialApp?.appKey} initialStatuses={initialApp?.statuses} onTable={onAppTable} />
            )}
            {headers.length > 0 && (
              <>
                {problem && <p className="text-xs text-domino-aubergine">{problem}</p>}
                {rows.length === 0
                  ? <p className="text-sm text-domino-inkSoft">Aucune ligne</p>
                  : <ImportPreviewTable headers={headers} rows={rows} mapping={mapping} onMapping={setColumns} />}
                {rows.length > 0 && (allowNoCampaign || initialCampaign !== null) && (
                  <CampaignChoice toolAppId={toolAppId} value={campaign} suggestedName={suggestedName} allowNone={allowNoCampaign} onChange={setCampaign} />
                )}
              </>
            )}
          </>
        )}
      </div>
    </SideDrawer>
  );
};
